import { Info } from "@mui/icons-material";
import {
    IconButton,
    ImageListItem,
    ImageListItemBar
} from "@mui/material";
import React, { useState } from "react";
import { EActiveDrawer } from "../../@types/Enums";
import { EPendingType } from "../../@types/PendingItemTypes";
import useResponsiveSizes from "../../hooks/useResponsiveSizes";
import FilesAPI2DModel from "../../models/standard/FilesAPI2DModel";
import { useModel2DActions } from "../../provider/BuildingProvider/actions/useModel2DActions";
import { useBuilding } from "../../provider/BuildingProvider/BuildingProvider";
import { usePendingItem } from "../../provider/PendingItemProvider"; // Import Pending Logic
import { useSelection } from "../../provider/SelectionProvider";
import {
    getProductImageListItemBarStyles,
    getProductImageListItemIconButtonStyles,
    getProductImageListItemStyles
} from "../../utils/imgUtils";
import { createModel2DFromApi } from "../../utils/modelFactory"; // Import Factory
import LazyImage from "./LazyImage";
import RoomComponentTooltipContainer from "./RoomComponentTooltipContainer";
import Tooltip from "./Tooltip";

interface Model2DImageListItemProps {
    model2D: FilesAPI2DModel;
    index: number;
}

const Model2DImageListItem = ({ model2D, index }: Model2DImageListItemProps): JSX.Element => {
    const { setBuilding } = useBuilding();
    // Use the specific action hook that handles Root vs Space logic
    const { addModel2D } = useModel2DActions(setBuilding);
    const { startPlacement } = usePendingItem();
    const sizes = useResponsiveSizes();
    const { setActiveDrawer } = useSelection();

    // Loading state for when fetching SVG/Dims
    const [isLoading, setIsLoading] = useState(false);

    const handleClick = async () => {
        if (isLoading) return;
        setIsLoading(true);

        // 1. Fetch SVG and Create the Internal Model
        const fullModel = await createModel2DFromApi(model2D);
        
        setIsLoading(false);

        if (!fullModel) {
            console.error("Failed to load 2D Model details");
            return;
        }

        // 2. Start Ghost Placement
        startPlacement({
            type: EPendingType.MODEL_2D,
            data: { model2D: fullModel }, // Pass the fully formed model
            
            // 3. Confirm Placement (Called when clicking on the floor)
            onConfirm: (position, spaceUuid) => {
                // Update position one last time
                fullModel.position = position;
                // Add to building state (Space or Root)
                addModel2D(fullModel, spaceUuid);
            }
        });
        setActiveDrawer(EActiveDrawer.NONE)
    };

    return (
        <ImageListItem
            cols={1}
            rows={1}
            key={`${model2D.fileName}-${index}`}
            sx={{
                ...getProductImageListItemStyles(),
                // Visual feedback for loading
                opacity: isLoading ? 0.5 : 1,
                cursor: isLoading ? 'wait' : 'pointer'
            }}  
            onClick={handleClick}
        >
            <LazyImage item={model2D} />
            
            <ImageListItemBar
                key={`${model2D.name}-imgBar`}
                className={"imgBar"}
                title={model2D.name}
                subtitle={""}  // No manufacturer
                actionIcon={
                    <Tooltip
                        PopperProps={{
                            modifiers: [
                                { name: "preventOverflow", options: { boundary: "window" } },
                                { name: "offset", options: { offset: [8, 0] } },
                            ],
                        }}
                        title={<RoomComponentTooltipContainer roomComponent={model2D} />}
                        placement="top-start"
                    >
                        <IconButton
                            className={"iconBtn"}
                            sx={getProductImageListItemIconButtonStyles()}
                            aria-label={`info about ${model2D.name}`}
                        >
                            <Info />
                        </IconButton>
                    </Tooltip>
                }
                sx={getProductImageListItemBarStyles()}
            />
        </ImageListItem>
    );
};

export default Model2DImageListItem;